import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/format";

export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const checkoutId = params.get("checkout_id");
  const [status, setStatus] = useState<"loading" | "ok" | "pending" | "error">("loading");
  const [total, setTotal] = useState<string | null>(null);

  useEffect(() => {
    if (!checkoutId) {
      setStatus("ok");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const checkout = await api.get<{ status: string; total_amount: string }>(
          `/api/v1/public/shop/checkout/${checkoutId}`,
        );
        if (cancelled) return;
        setTotal(checkout.total_amount);
        if (checkout.status === "completed") {
          setStatus("ok");
        } else {
          setStatus("pending");
          window.setTimeout(() => void poll(), 2000);
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [checkoutId]);

  return (
    <ShopLayout>
      <div className="shop-page flex justify-center">
        <div className="shop-card w-full max-w-md animate-slide-up text-center">
          {status === "loading" || status === "pending" ? (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center">
                <span className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Zahlung wird bestätigt…</h1>
              <p className="mt-3 text-sm text-slate-600">Bitte einen Moment warten.</p>
            </>
          ) : status === "error" ? (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-slate-900">Bestätigung ausstehend</h1>
              <p className="mt-3 text-sm text-slate-600">
                Ihre Zahlung wurde angenommen. Die Bestellung erscheint in Kürze in unserem System.
              </p>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-700">
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-green-800 sm:text-2xl">Vielen Dank!</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Ihre Vorbestellung wurde bezahlt
                {total ? ` (${formatPrice(total)})` : ""}. Abholartikel bereiten wir zum vereinbarten
                Termin vor.
              </p>
            </>
          )}
          <Link to="/" className="shop-btn-primary mt-8 inline-flex">
            Weitere Artikel bestellen
          </Link>
        </div>
      </div>
    </ShopLayout>
  );
}
